import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import type { UploadFile } from 'element-plus'
import type { IFragmentFilters } from '../shared/filters'

export enum Environment {
  ModularDataCenter = 'Modular',
  StandaloneBuilding = 'Traditional',
}

export enum CoolingMethod {
  AirCooled = 'Air',
  Immersion = 'Immersion',
  Water = 'Water',
}

export enum PolicyStatus {
  PendingReview = 'Pending Review',
  Active = 'Active',
  Suspended = 'Suspended',
  Inactive = 'Inactive',
  Terminated = 'Terminated',
  Cancelled = 'Cancelled',
}

export enum PolicyGroupStatus {
  Active = 'Active',
  Suspended = 'Suspended',
  Inactive = 'Inactive',
  Terminated = 'Terminated',
  Cancelled = 'Cancelled',
}

export interface IPolicyGroupUpdateForm {
  effective_date: Date
  initial_term_months: number
  subsequent_term_months: number
  status: PolicyGroupStatus
  new_signed_document?: UploadFile
}

export interface IPolicyGroupForm {
  groupNumber?: string
  customerID?: number
  creationDate?: Date
  initialTerm: number
  subsequentTerm: number
  serviceAgreement?: UploadFile
}

export interface IPolicyDetailsForm {
  location?: ISiteIdentifier
  environment: Environment
  coolingMethod: CoolingMethod
  running3rdPartySoft: boolean
  overclockingMiners: boolean
  numberOfPolicies?: number
  addByCSV: boolean
  minersCSV?: UploadFile
}

export interface IPolicyMinerEntry {
  model_family?: string
  sn: string
  policySalePrice: number
  environment: Environment
  coolingMethod: CoolingMethod
  running3rdPartySoft: boolean
  overclockingMiners: boolean
  number: number // for list rendering key only
}

export interface IPolicyGroupCreationForm {
  group: IPolicyGroupForm
  details: IPolicyDetailsForm
  miners: IPolicyMinerEntry[]
}

export interface IPolicyGroupCreationResponse {
  id: number
  signed_doc_url: string
  number: string
  effective_date: Date
  initial_term_months: number
  subsequent_term_months: number
  created_at: Date
  customer: number
}

export interface IPolicyBulkUpdateForm {
  policy_ids: number[]
  sale_price?: number
  status?: PolicyStatus
  site_id?: string
  facility_type?: Environment
  cooling_type?: CoolingMethod
  third_party_firmware_used?: boolean
  overclocking?: boolean
}

interface PolicyStatusCount {
  [status: string]: number
}

export interface IPolicyGroup {
  id: number
  signed_doc_url: string
  customer: {
    id: number
    name: string
  }
  total_policies: number
  status: PolicyGroupStatus
  policies_by_status: PolicyStatusCount
  number: string
  effective_date: string
  initial_term_months: number
  subsequent_term_months: number
  created_at: Date
}

export interface IPolicy {
  id: number
  customer: {
    id: number
    name: string
  }
  site: {
    id: number
    name: string
    address: string | null
  }
  term: {
    effective_date: Date
    initial_term_months: number
    subsequent_term_months: number
  }
  insured_miner: {
    id: number
    sn: string
    model: string
    model_family: string
    cooling_type: CoolingMethod
    facility_type: Environment
    third_party_firmware_used: boolean
    overclocking: boolean
    policy: number
  }
  group_number: string
  end_date: null
  number: string
  sku: string
  price: string
  status: PolicyStatus
  created_at: Date
  updated_at: Date
  group: number
}

export interface IPolicyFilters extends IFragmentFilters {
  customer_id?: number
  number?: string
  policy_group_number?: string
  status?: string
  fragment?: string
}
